"use client";

import React, { useRef, useState, useCallback } from "react";
import styles from "../css/components/ImageDropzone.module.css";

/**
 * ImageDropzone
 * - Drag & drop or click-to-browse single image upload (PNG, JPG by default)
 * - Controlled by `value` (File | null) and `onChange` (event-like or file callback)
 */
export default function ImageDropzone({
  id = "image-dropzone",
  name = "image",
  accept = "image/png,image/jpeg",
  value = null,
  viewUrl = "",
  disabled = false,
  onChange,
  placeholder = "Drop files here",
  helper = "Supported format: PNG, JPG",
  className = "",
}) {
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const normalizedViewUrl = typeof viewUrl === "string" ? viewUrl.trim() : "";
  const canView = normalizedViewUrl.length > 0;
  const canSelectFile = !disabled && !canView;

  const openFilePicker = () => {
    if (!canSelectFile) return;
    inputRef.current?.click();
  };

  const handleFiles = useCallback(
    (files) => {
      const file = files?.[0] || null;
      if (!file) return;
      // Ensure file type matches accept if provided
      if (accept && !accept.split(",").some((t) => file.type === t.trim())) {
        // Fallback: allow common extensions check
        const allowed = [".png", ".jpg", ".jpeg"]; 
        const lower = file.name.toLowerCase();
        if (!allowed.some((ext) => lower.endsWith(ext))) return;
      }
      if (typeof onChange === "function") {
        // Create a synthetic event compatible with existing handlers
        const syntheticEvent = {
          target: { files: [file], name, id },
          currentTarget: { files: [file], name, id },
          preventDefault: () => {},
          stopPropagation: () => {},
        };
        onChange(syntheticEvent);
      }
    },
    [accept, id, name, onChange]
  );

  const onDrop = (e) => {
    if (!canSelectFile) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onDragOver = (e) => {
    if (!canSelectFile) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const onDragLeave = (e) => {
    if (!canSelectFile) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const onInputChange = (e) => {
    handleFiles(e.target.files);
  };

  return (
    <div
      className={`${styles.dropzone} ${isDragOver ? styles.dragOver : ""} ${className}`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      role="button"
      tabIndex={0}
      onClick={canSelectFile ? openFilePicker : undefined}
      aria-label={placeholder}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="file"
        accept={accept}
        className={styles.hiddenInput}
        onChange={onInputChange}
        disabled={!canSelectFile}
      />

      <div className={styles.iconWrap} aria-hidden>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 3v12m0-12l-4 4m4-4l4 4" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M20 16.5a3.5 3.5 0 01-3.5 3.5h-9A3.5 3.5 0 014 16.5c0-1.93 1.57-3.5 3.5-3.5.23 0 .46.02.68.07A5 5 0 0112 8a5 5 0 013.82 5.07c.22-.05.45-.07.68-.07A3.5 3.5 0 0120 16.5z" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>

      <div className={styles.title}>{placeholder}</div>
      <div className={styles.helper}>{helper}</div>
      <div className={styles.or}>OR</div>
      {canView ? (
        <button
          type="button"
          className={styles.browseBtn}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(normalizedViewUrl, "_blank", "noopener,noreferrer");
          }}
        >
          View
        </button>
      ) : (
        <button type="button" className={styles.browseBtn} onClick={openFilePicker} disabled={!canSelectFile}>
          Browse files
        </button>
      )}

      {value?.name && (
        <div className={styles.fileName} title={value.name}>
          Selected: {value.name}
        </div>
      )}
    </div>
  );
}
